import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('redex_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface EmailLog {
  id: number;
  to_email: string;
  cc_emails?: string;
  bcc_emails?: string;
  subject: string;
  html_body: string;
  text_body?: string;
  status: string;
  message_id?: string;
  error_message?: string;
  sent_at?: string;
  created_at: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  description: string;
  variables: string[];
}

export interface SendEmailData {
  to: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  cc?: string[];
  bcc?: string[];
}

export interface BulkSendEmailData {
  recipients: string[];
  subject: string;
  htmlBody: string;
  textBody?: string;
}

export interface SendingQuota {
  max24HourSend: number;
  maxSendRate: number;
  sentLast24Hours: number;
}

export interface GetEmailsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Service methods
export const emailsService = {
  // Get all email logs with pagination
  async getEmails(params: GetEmailsParams = {}): Promise<{ data: EmailLog[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await api.get(`/emails?${queryParams.toString()}`);
    return {
      data: response.data.data.data,
      pagination: {
        total: response.data.data.total,
        page: response.data.data.page,
        limit: response.data.data.limit,
        totalPages: response.data.data.totalPages,
      },
    };
  },

  // Send a single email
  async sendEmail(data: SendEmailData): Promise<{ emailId: number; messageId: string; status: string }> {
    const response = await api.post('/emails/send', data);
    return response.data.data;
  },

  // Send bulk emails
  async sendBulkEmail(data: BulkSendEmailData): Promise<{
    results: Array<{ recipient: string; success: boolean; messageId?: string; error?: string }>;
    summary: { total: number; successful: number; failed: number };
  }> {
    const response = await api.post('/emails/bulk-send', data);
    return response.data.data;
  },

  // Get email templates
  async getTemplates(): Promise<EmailTemplate[]> {
    const response = await api.get('/emails/templates');
    return response.data.data;
  },

  // Get sending quota
  async getSendingQuota(): Promise<SendingQuota> {
    const response = await api.get('/emails/quota');
    return response.data.data;
  },
};

export default emailsService;
